import { useEffect, useRef, useState } from 'react';
import Flipbook from './Flipbook';

const IMG = {
  restaurant1: '/assets/pod-restaurant1.jpg',
  restaurant2: '/assets/pod-restaurant2.jpg',
  zen: '/assets/pod-zen.jpg',
  podDay: '/assets/pod-day.jpg',
  podNight: '/assets/pod-night.jpg',
};

// Hook: returns scroll progress 0..1 across the referenced section
function useSectionProgress(ref) {
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf;
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const total = el.offsetHeight - window.innerHeight;
        const traveled = -rect.top;
        const v = Math.min(1, Math.max(0, traveled / Math.max(1, total)));
        setP(v);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [ref]);
  return p;
}

function ParallaxImage({ src, alt, speed = 0.18, className = '', testid }) {
  const ref = useRef(null);
  useEffect(() => {
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2 - window.innerHeight / 2;
        el.style.transform = `translate3d(0, ${-center * speed}px, 0) scale(1.06)`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [speed]);
  return (
    <div className={`overflow-hidden ${className}`}>
      <img
        ref={ref}
        src={src}
        alt={alt}
        data-testid={testid}
        className="w-full h-full object-cover will-change-transform"
        loading="lazy"
      />
    </div>
  );
}

// Parallax that preserves natural aspect ratio (used for full-width / responsive height-auto images).
// Scales the image up slightly and translates Y based on viewport position, clipped by the wrapper.
function NaturalParallax({ src, alt, testid, intensity = 50, scale = 1.08, className = '', wrapperClassName = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el || !el.parentElement) return;
        const rect = el.parentElement.getBoundingClientRect();
        const vh = window.innerHeight;
        // -1 (below viewport) → 0 (centered) → +1 (above viewport)
        const span = vh / 2 + rect.height / 2;
        if (span <= 0) return;
        const p = (rect.top + rect.height / 2 - vh / 2) / span;
        const clamped = Math.max(-1, Math.min(1, p));
        el.style.transform = `translate3d(0, ${-clamped * intensity}px, 0) scale(${scale})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [intensity, scale]);
  return (
    <div className={`relative w-full overflow-hidden ${wrapperClassName}`}>
      <img
        ref={ref}
        src={src}
        alt={alt}
        data-testid={testid}
        className={`block w-full h-auto will-change-transform ${className}`}
        loading="lazy"
      />
    </div>
  );
}

// Sticky day/night pod reveal
function PodReveal() {
  const wrapRef = useRef(null);
  const progress = useSectionProgress(wrapRef);
  // map progress 0..1 to clip; we want night to wipe in from the right
  const clip = `inset(0 ${Math.round((1 - progress) * 100)}% 0 0)`;

  return (
    <div
      ref={wrapRef}
      data-testid="pod-daynight-wrap"
      className="relative w-full"
      style={{ height: '260vh' }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-bg">
        {/* Day image */}
        <img
          src={IMG.podDay}
          alt="Pod Bristol bathroom pod in daylight"
          data-testid="pod-day-img"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Night image clipped */}
        <img
          src={IMG.podNight}
          alt="Pod Bristol bathroom pod at dusk"
          data-testid="pod-night-img"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ clipPath: clip, WebkitClipPath: clip, transition: 'clip-path 60ms linear' }}
        />

        {/* Diagonal wipe line */}
        <div
          className="absolute top-0 bottom-0 w-px bg-bg/70 mix-blend-overlay pointer-events-none"
          style={{ left: `${progress * 100}%` }}
        />

        {/* Top labels */}
        <div className="absolute top-0 left-0 right-0 px-6 md:px-12 pt-8 md:pt-12 flex items-center justify-between text-bg">
          <div className="kicker !text-bg/80">Day · 07:14</div>
          <div className="kicker !text-bg/80">Dusk · 19:42</div>
        </div>

        {/* Center caption */}
        <div className="absolute inset-x-0 bottom-12 md:bottom-16 px-6 md:px-12 flex items-end justify-between text-bg">
          <h3 className="font-display text-4xl md:text-6xl tracking-tight max-w-2xl leading-[1.02]">
            One pod.<br />
            <span className="italic">Two atmospheres.</span>
          </h3>
          <div className="hidden md:block max-w-sm text-sm leading-relaxed text-bg/90">
            A self-contained micro architecture in tinted concrete and stained walnut. The same volume
            shapeshifts with light — calm and clinical at dawn, intimate and cinematic at dusk.
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-bg/80 text-[10px] tracking-[0.32em] uppercase">
          Scroll to wake the pod
        </div>
      </div>
    </div>
  );
}

export default function PodBristol() {
  return (
    <section
      id="pod-bristol"
      data-testid="pod-bristol-section"
      className="relative bg-bg"
    >
      {/* Intro block */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 pt-12 md:pt-16 pb-16 md:pb-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div className="reveal">
            <span className="kicker" data-testid="pod-kicker">2026 / Featured</span>
            <h2
              data-testid="pod-title"
              className="font-display font-light tracking-tight text-ink leading-[0.92] text-[clamp(3.4rem,11vw,10rem)] mt-4"
            >
              Pod <span className="italic">Bristol</span>
            </h2>
          </div>
          <div className="reveal md:max-w-md text-base md:text-lg leading-relaxed text-sub">
            A capsule pod hotel for Bristol — modular, communal, and rooted in Japanese
            design principles.
          </div>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
          {[
            ['Type', 'Hospitality · Restaurant + Wellness'],
            ['Location', 'Bristol, UK'],
            ['Year', '2026'],
            ['Role', 'Concept, Design & Materials'],
          ].map(([k, v]) => (
            <div key={k} className="reveal">
              <div className="kicker">{k}</div>
              <div className="mt-2 font-display text-xl md:text-2xl text-ink leading-tight">{v}</div>
            </div>
          ))}
        </div>

        {/* Project Brief */}
        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-3 reveal">
            <span className="kicker">Project Brief</span>
          </div>
          <div className="col-span-12 md:col-span-8 reveal space-y-5 text-base md:text-lg leading-relaxed text-sub">
            <p>
              This project proposes the adaptive reuse of the Old Fire Station on Silver Street,
              Bristol, into a design-led capsule pod hotel inspired by Japanese capsule hotels and
              Metabolist architecture. The scheme will provide 30–40 compact sleeping pods alongside
              shared spaces including a café, bar and zen garden, creating affordable and
              community-focused accommodation within Bristol city centre.
            </p>
            <p>
              The project explores capsule living as a response to urban density, housing challenges
              and the demand for affordable short-stay accommodation. Designed primarily for
              students, budget travellers and tourists, the scheme also considers temporary housing
              for individuals experiencing housing insecurity.
            </p>
            <p>
              Sustainability is central to the proposal through the reuse of the historic building,
              preserving its heritage while introducing modular, efficient and contemporary living
              spaces influenced by Japanese design principles. The project aims to balance privacy
              and social interaction through compact pods and shared communal environments.
            </p>
          </div>
        </div>
      </div>

      {/* Full-width hero visual */}
      <NaturalParallax
        src="/assets/pod-main-visual.jpg"
        alt="Pod Bristol — main pods interior overview"
        testid="pod-main-visual"
        intensity={70}
        scale={1.1}
        wrapperClassName="reveal"
      />

      {/* Sleeping pod — image + descriptive copy */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-32 grid grid-cols-12 gap-6 md:gap-10 items-center">
        <div className="col-span-12 md:col-span-7 reveal">
          <NaturalParallax
            src="/assets/pod-sleeping.jpg"
            alt="Pod Bristol — moulded fibreglass sleeping pod with oak interior"
            testid="pod-sleeping-image"
            intensity={50}
            scale={1.1}
            wrapperClassName="rounded-sm bg-surface"
          />
        </div>
        <div className="col-span-12 md:col-span-5 md:pl-6 reveal">
          <span className="kicker">The Sleeping Pod</span>
          <h3 className="font-display text-4xl md:text-5xl tracking-tight mt-4 leading-[1.05]">
            Designed for <span className="italic">rest</span><br />and quiet.
          </h3>
          <div className="mt-6 space-y-5 text-base md:text-lg leading-relaxed text-sub">
            <p>
              The sleeping pods were designed to create a private and calming environment focused
              on rest and relaxation. Two pod forms were developed within the design, both providing
              the same internal function and capacity but differing in their entrance and opening
              systems. This variation adds visual interest while maintaining consistency throughout
              the space.
            </p>
            <p>
              The pods are supported by a durable metal frame, allowing them to remain stable and
              potentially be stacked within compact accommodation environments. The exterior shell
              is formed from moulded fibreglass with a polished concrete finish, creating a clean
              and minimalist appearance that reflects the industrial character of the overall
              project.
            </p>
          </div>

          <ul className="mt-10 space-y-3 text-sm text-ink/85">
            <li className="flex items-start gap-3"><span className="mt-2 h-px w-6 bg-ink/40" /> Two interchangeable entry typologies</li>
            <li className="flex items-start gap-3"><span className="mt-2 h-px w-6 bg-ink/40" /> Moulded fibreglass shell · polished concrete finish</li>
            <li className="flex items-start gap-3"><span className="mt-2 h-px w-6 bg-ink/40" /> Steel frame · stackable in compact stays</li>
          </ul>
        </div>
      </div>

      {/* Pod variations grid */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 pb-20 md:pb-32 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <figure className="reveal">
          <ParallaxImage
            src="/assets/pod-variation1.jpg"
            alt="Pod Bristol — alternative pod opening, side view"
            testid="pod-variation-1"
            speed={0.12}
            className="aspect-square rounded-sm bg-surface"
          />
          <figcaption className="mt-4 flex items-center justify-between text-xs tracking-[0.28em] uppercase text-sub">
            <span>Pod · Type A</span>
            <span>Lateral entry</span>
          </figcaption>
        </figure>

        <figure className="reveal">
          <ParallaxImage
            src="/assets/pod-variation2.jpg"
            alt="Pod Bristol — pod exterior detail"
            testid="pod-variation-2"
            speed={0.16}
            className="aspect-square rounded-sm bg-surface"
          />
          <figcaption className="mt-4 flex items-center justify-between text-xs tracking-[0.28em] uppercase text-sub">
            <span>Pod · Type B</span>
            <span>Frontal opening</span>
          </figcaption>
        </figure>
      </div>

      {/* Bathing pods chapter heading */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 mt-12 md:mt-24 mb-10 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-7 md:col-start-2 reveal">
          <span className="kicker">Chapter 03 — Wellness Pods</span>
          <h3 className="font-display text-5xl md:text-7xl tracking-tight mt-4 leading-[0.98]">
            The <span className="italic">bathing</span><br />capsule.
          </h3>
          <p className="mt-6 max-w-xl text-sub leading-relaxed">
            Each pod is a single moulded volume — concrete shell, walnut throat. Below, watch
            the same room slip from morning daylight into evening firelight as you scroll.
          </p>
        </div>
      </div>

      {/* Sticky Day/Night reveal */}
      <PodReveal />

      {/* Lobby — image + descriptive copy */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-32 grid grid-cols-12 gap-6 md:gap-10 items-center">
        <div className="col-span-12 md:col-span-7 reveal">
          <NaturalParallax
            src="/assets/pod-lobby-detail.jpg"
            alt="Pod Bristol — lobby reception desk with bonsai and slatted wood"
            testid="pod-lobby-detail"
            intensity={50}
            scale={1.1}
            wrapperClassName="rounded-sm bg-surface"
          />
        </div>
        <div className="col-span-12 md:col-span-5 md:pl-6 reveal">
          <span className="kicker">The Lobby</span>
          <h3 className="font-display text-4xl md:text-5xl tracking-tight mt-4 leading-[1.05]">
            A <span className="italic text-crimson">zen-inspired</span><br />social hub.
          </h3>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-sub">
            The lobby serves as the heart of the design, offering a calm, easily navigable starting
            point that connects all areas of the building. More than just an entrance, it acts as a
            welcoming social hub that establishes a peaceful tone for the visitor’s entire journey.
          </p>

          <h4 className="kicker mt-10">Key design elements</h4>
          <ul className="mt-4 space-y-5 text-sub leading-relaxed">
            <li className="flex gap-4">
              <span className="mt-2.5 block h-px w-6 bg-ink/40 shrink-0" />
              <span>
                <span className="text-ink font-medium">Traditional Japanese aesthetic.</span>{' '}
                Minimalist furniture, indoor plants and natural textures — wood panelling and stone
                — work together to create a tranquil environment.
              </span>
            </li>
            <li className="flex gap-4">
              <span className="mt-2.5 block h-px w-6 bg-ink/40 shrink-0" />
              <span>
                <span className="text-ink font-medium">Indoor–outdoor harmony.</span>{' '}
                Red-framed windows, inspired by Japanese architecture, flood the open seating area
                with natural light and connect the interior to the outside world.
              </span>
            </li>
            <li className="flex gap-4">
              <span className="mt-2.5 block h-px w-6 bg-ink/40 shrink-0" />
              <span>
                <span className="text-ink font-medium">Reception focal point.</span>{' '}
                A central desk in vertical wooden slats and dark stone, accented by bonsai-style
                trees and mountain-inspired wall art — reinforcing the calming Zen atmosphere.
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Lobby — full-width visual */}
      <NaturalParallax
        src="/assets/pod-lobby-wide.jpg"
        alt="Pod Bristol — lobby wide view"
        testid="pod-lobby-wide"
        intensity={80}
        scale={1.12}
        wrapperClassName="reveal"
      />

      {/* Restaurant — chapter 1 */}
      <div className="relative">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-10 md:py-16 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-5 md:pt-24 reveal">
            <span className="kicker">The Pod Restaurant</span>
            <h3 className="font-display text-4xl md:text-5xl tracking-tight mt-3 leading-[1.05]">
              Luxurious yet <span className="italic text-crimson">accessible</span>.
            </h3>
            <div className="mt-5 space-y-4 text-sub leading-relaxed">
              <p>
                The restaurant was designed to provide a luxurious yet accessible dining experience,
                combining high-end aesthetics with a welcoming atmosphere. As part of the project’s
                community-focused vision, a percentage of the restaurant’s profits will support
                homeless shelters and local initiatives.
              </p>
              <p>
                Inspired by Japanese design principles, the interior features rich red tones,
                natural wood finishes, marble surfaces, and warm lighting to create a sophisticated
                and inviting environment. Curved wooden dividers define semi-private dining areas
                while maintaining a sense of openness, and a sculptural central light installation
                serves as a striking focal point.
              </p>
              <p>
                The layout accommodates a variety of users, from solo visitors to larger groups,
                through a mix of booth seating and open dining spaces. By blending contemporary
                Japanese-inspired design with social responsibility, the restaurant offers a
                memorable dining experience that promotes connection, wellbeing, and positive
                community impact.
              </p>
            </div>
          </div>
          <ParallaxImage
            src={IMG.restaurant1}
            alt="Pod Bristol restaurant dining room with red laths and chandelier"
            testid="pod-restaurant-1"
            className="col-span-12 md:col-span-7 aspect-[4/3] md:aspect-[5/4] rounded-sm"
            speed={0.12}
          />
        </div>

        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-6 md:py-16 grid grid-cols-12 gap-6">
          <ParallaxImage
            src={IMG.restaurant2}
            alt="Pod Bristol restaurant booth seating detail"
            testid="pod-restaurant-2"
            className="col-span-12 md:col-span-7 md:col-start-1 aspect-[5/4] rounded-sm"
            speed={0.18}
          />
          <div className="col-span-12 md:col-span-4 md:col-start-9 md:pt-24 reveal">
            <span className="kicker">Detail</span>
            <p className="mt-3 font-display text-2xl md:text-3xl text-ink leading-snug">
              “Materials are the second language of architecture — I let the wood and stone
              finish each other’s sentences.”
            </p>
            <span className="mt-4 inline-block text-xs tracking-[0.3em] uppercase text-sub">— Joanna</span>
          </div>
        </div>
      </div>

      {/* Zen Garden — chapter 2 */}
      <div className="relative mt-10 md:mt-20">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-5 reveal">
            <span className="kicker">Zen Garden</span>
            <h3 className="font-display text-4xl md:text-5xl tracking-tight mt-3 leading-[1.05]">
              A pause between<br />
              <span className="italic">two interiors</span>.
            </h3>
            <div className="mt-5 space-y-4 text-sub leading-relaxed">
              <p>
                The Zen garden is designed as a peaceful public space where people can escape from
                the stress and pressure of everyday life. Although the garden is open to the public,
                the number of visitors allowed inside at one time is limited to maintain a calm and
                relaxing atmosphere. By controlling the amount of people in the space, visitors are
                able to experience the quiet environment without overcrowding or noise, helping them
                feel more connected to nature and their surroundings.
              </p>
              <p>
                The garden is designed to be a place where people can take a break from their
                worries, clear their minds, and relax mentally and emotionally. Features such as
                trees, ponds, stepping stones, and open spaces create a calming environment inspired
                by traditional Zen garden design principles. The peaceful layout encourages slow
                movement, mindfulness, and reflection, allowing visitors to temporarily step away
                from busy city life and focus on their wellbeing.
              </p>
            </div>
          </div>
          <ParallaxImage
            src={IMG.zen}
            alt="Pod Bristol zen garden courtyard with cherry blossom and red lattice"
            testid="pod-zen"
            className="col-span-12 md:col-span-7 aspect-[4/3] md:aspect-[16/10] rounded-sm"
            speed={0.1}
          />
        </div>
      </div>

      {/* Drawing Packages — flipbook */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40 grid grid-cols-12 gap-8 md:gap-12 items-start">
        <div className="col-span-12 md:col-span-4 reveal md:sticky md:top-28">
          <span className="kicker">Documentation</span>
          <h3 className="font-display text-4xl md:text-5xl tracking-tight mt-4 leading-[1.05]">
            Drawing<br /><span className="italic">Packages</span>.
          </h3>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-sub">
            Explore the complete project documentation, including floor plans, floor finish
            schedules, construction details, and pod specifications, all available in the
            interactive flipbook.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-ink/85">
            <li className="flex items-start gap-3"><span className="mt-2 h-px w-6 bg-ink/40" /> Drag the corners or use the arrows</li>
            <li className="flex items-start gap-3"><span className="mt-2 h-px w-6 bg-ink/40" /> Floor plans &amp; finish schedules</li>
            <li className="flex items-start gap-3"><span className="mt-2 h-px w-6 bg-ink/40" /> Construction &amp; pod specifications</li>
          </ul>
        </div>
        <div className="col-span-12 md:col-span-8 reveal">
          <Flipbook pdfUrl="/assets/pod-plans.pdf" />
        </div>
      </div>

      {/* Research & Development — full-width flipbook */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 pb-24 md:pb-40">
        <div className="reveal max-w-3xl mb-10 md:mb-14">
          <span className="kicker">Process</span>
          <h3 className="font-display text-4xl md:text-5xl tracking-tight mt-3 leading-[1.05]">
            Research &amp; <span className="italic">Development</span>.
          </h3>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-sub">
            This book presents the research and development process behind my design project. It
            documents the journey from the earliest ideas and investigations to the development of
            the final design proposal. Through research, experimentation, concept generation, and
            refinement, I explored different approaches to create a solution that responds to both
            functional and aesthetic requirements. The following pages illustrate the evolution of
            my ideas and the design decisions that informed the final outcome.
          </p>
        </div>
        <div className="reveal">
          <Flipbook
            pdfUrl="/assets/pod-rd.pdf"
            kicker="Process Book"
            title="Research & Development"
            subtitle="Earliest ideas · investigations · experiments · concept generation · refinement."
          />
        </div>
      </div>

      {/* Outro */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-6 reveal">
          <span className="kicker">Coda</span>
          <h4 className="font-display text-3xl md:text-4xl mt-3 leading-snug text-ink">
            Pod Bristol is, in the end, a thesis on threshold — how a guest crosses light,
            material and silence in a single evening.
          </h4>
        </div>
        <div className="col-span-12 md:col-span-4 md:col-start-9 reveal">
          <div className="kicker">Next</div>
          <button
            data-testid="pod-to-about"
            className="mt-3 font-display text-3xl md:text-4xl text-ink hover:text-crimson transition-colors"
            onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Meet the designer →
          </button>
        </div>
      </div>
    </section>
  );
}
